import React, { useState, useRef, useEffect } from "react";
import { useSelector } from "react-redux";
import { message, Popover, DatePicker, Alert } from "antd";
import dayjs from "dayjs";
import { QuestionCircleOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import moment from "moment";
import LoadingOverlay from "../../Overlays/LoadingOverlay";
import { fetchWrapper } from "../../../helpers";

// Custom hook to work with the moderation state
import { useModeratorPost } from "../../../hooks/useModeratorPost";

// Icons (you'll need to import these)
import copyIcon from "../../../pages/icons/copy-icon.svg";
import penIcon from "../../../pages/icons/pen-icon.svg";
import checkIcon from "../../../pages/icons/check-icon-black.svg";

// Imported for error emulation - remove before pushing to production
import { useDispatch } from "react-redux";
import { distActions } from "../../../store"; // adjust if your path differs
import PostRegenerateEditor from "./PostRegenerateEditor";

const PostContentEditorDetailsV2 = ({ employeeId }) => {
    // Remove dispatch before pushing to production - it's for error emulation
    const dispatch = useDispatch();

    const [publishTime, setPublishTime] = useState(Date.now());
    const [autopublication, setAutopublication] = useState(false);

    const { t } = useTranslation();

    // Use the employee social media
    const employeeSocialMediaMap = useSelector((state) => state.dist.employeeSocialMediaMap);
    const connectedPlatforms = employeeSocialMediaMap?.[employeeId] || [];

    // Use the custom hook
    const { employee, currentPost, tempImages, tempVideo, isGenerating, initializePost, updatePostContent, addImages, removeImage, clearImages, setVideo, clearVideo, regeneratePost, submitPost, validatePost, toggleSocialNetwork } = useModeratorPost(employeeId);

    // Social media icons in case a social media account is present for particular employee
    const renderSocialIcon = (platform, src, label) => {
        const isSelected = Array.isArray(currentPost.socialNetwork) && currentPost.socialNetwork.includes(platform);

        return (
            <div key={platform} className={`flex flex-col items-center cursor-pointer rounded-lg p-2 border-2 transition-all duration-200 ${isSelected ? "border-purple-600 bg-purple-100 shadow" : "border-transparent hover:border-gray-300"}`} onClick={() => toggleSocialNetwork(platform)} title={`Кликните, чтобы ${isSelected ? "снять" : "выбрать"} ${label}`}>
                <img loading="lazy" src={src} alt={`${platform} logo`} className="w-6 aspect-square" />
                <div className="mt-1 text-sm text-center">{label}</div>
            </div>
        );
    };

    const socialIcons = [
        {
            platform: "LINKEDIN",
            src: "https://cdn.builder.io/api/v1/image/assets/TEMP/d25408c5dff59b86cb5e75674b2a798a87cd455bde0adf0ceb8c9a670d1405e8?apiKey=300ff06bbf3a4f5c9c73ca7577720bf1&",
            label: "LinkedIn",
        },
        {
            platform: "FACEBOOK",
            src: "https://cdn.builder.io/api/v1/image/assets/TEMP/f8432315b7e3752ab28ffd9571f771342eef6b676c1f20e4b8a5e6ee48c70349?apiKey=300ff06bbf3a4f5c9c73ca7577720bf1&",
            label: "Facebook",
        },
        {
            platform: "INSTAGRAM",
            src: "https://cdn.builder.io/api/v1/image/assets/TEMP/497038a4df4615b7ef35db19c49364c0fdcd7e3749f9681fe807b55de2426e88?apiKey=300ff06bbf3a4f5c9c73ca7577720bf1&",
            label: "Instagram",
        },
    ];

    const visibleSocialIcons = socialIcons.filter(({ platform }) => connectedPlatforms.includes(platform));

    // Get job result from Redux
    const jobResult = useSelector((state) => state.dist.jobStatus[employeeId]?.result);

    // Local state for editing
    const [editMainText, setEditMainText] = useState(false);
    const [editHashtag, setEditHashtag] = useState(false);
    const [editOffer, setEditOffer] = useState(false);
    const [dragActive, setDragActive] = useState(false);

    // Indicate job status and error state
    const jobStatus = useSelector((state) => state.dist.jobStatus[employeeId]);
    const jobState = jobStatus?.status; // 'queued', 'delayed', 'failed', 'completed'
    const jobError = jobStatus?.error;
    const showOverlay = jobState === "queued" || jobState === "delayed" || jobState === "active";
    // Refs
    const hiddenVideoInput = useRef(null);
    const hiddenFileInput = useRef(null);
    const videoPlayerRef = useRef(null);

    // Initialize post data from job result
    useEffect(() => {
        if (jobResult) {
            initializePost({ ...jobResult }); // force new reference
        }
    }, [JSON.stringify(jobResult)]);

    if (!employee) return null;

    // Event handlers
    const changeMainText = () => {
        if (editMainText) {
            const text = document.querySelector(`#mainText-${employeeId}`).value;
            if (text.length > 2000) {
                message.error(t("functions.maxTextLength", { limit: 2000 }));
                return;
            }
            if (text.trim().length === 0) {
                message.error(t("functions.noText"));
                return;
            }
            if (text.trim().length < 25) {
                message.error(t("functions.minTextLength", { limit: 25 }));
                return;
            }
            updatePostContent({ post: text });
        }
        setEditMainText(!editMainText);
    };

    const changeHashtags = () => {
        if (editHashtag) {
            const hashtags = document.querySelector(`#hashtags-${employeeId}`).value;
            updatePostContent({ hashtags });
        }
        setEditHashtag(!editHashtag);
    };

    const changeOffer = () => {
        if (editOffer) {
            const media = document.querySelector(`#media-${employeeId}`).value;
            updatePostContent({ media: media.split(",").map((m) => m.trim()) });
        }
        setEditOffer(!editOffer);
    };

    const changePublishTime = (value) => {
        updatePostContent({ publishTime: value });
    };

    const clipboardCopy = (text) => {
        navigator.clipboard.writeText(text);
        message.success(t("functions.copied"));
    };

    // File handling
    const handleClick = () => {
        if (tempImages.length > 5) return message.error(t("functions.maxImages"));
        hiddenFileInput.current.click();
    };

    const handleChange = (event) => {
        if (event.target.files && event.target.files.length > 0) {
            addImages(event.target.files);
        }
    };

    const handleDeleteImage = (indexToDelete) => {
        if (window.confirm(t("post.removeSingleMediaConfirm"))) {
            removeImage(indexToDelete);
        }
    };

    const handleResetImages = () => {
        if (window.confirm(t("post.removeAllMediaConfirm"))) {
            clearImages();
        }
    };

    const handleAddVideo = (e) => {
        return message.info(t("functions.error"));
        // Video functionality disabled for now
    };

    const handleRemoveVideo = () => {
        clearVideo();
    };

    // Drag and drop handlers
    const handleDragEnter = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(true);
    };

    const handleDragLeave = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);
        if (e.dataTransfer.files && e.dataTransfer.files[0]) {
            let newFiles = Array.from(e.dataTransfer.files);
            let imageFiles = newFiles.filter((file) => file.type.includes("image"));
            if (imageFiles.length > 0) {
                addImages(imageFiles);
            }
        }
    };

    // Emulate post failure
    const emulateFailure = () => {
        dispatch(
            distActions.setJobStatus({
                employeeId,
                jobId: "debug-job-id",
                status: "failed",
                result: "⚠️ Генерация поста завершилась с ошибкой. Проверьте параметры запроса или попробуйте ещё раз.",
                error: "Error happened because of the hand-triggered error emulation",
            })
        );
    };

    return (
        <div className="relative rounded-xl overflow-hidden">
            <LoadingOverlay loading={showOverlay} message={`Status: Generating post…`}>
                <div className="relative">
                    <div>
                        <div
                            className="pb-20 md:pb-24"
                            style={{ display: "flex", flexDirection: "column", alignItems: "center" }}
                        >
                            {/* Error indication */}
                            {jobState === "failed" && (
                                <div className="w-full bg-red-50 border border-red-400 text-red-800 px-6 py-4 rounded-xl shadow-md mb-6 animate-pulse">
                                    <h3 className="text-lg font-semibold mb-2">⚠️ Post generation failed.</h3>
                                    <p className="text-sm whitespace-pre-line break-words mb-4">{jobStatus.error}</p>
                                    <div className="text-center">
                                        <PostRegenerateEditor
                                            onConfirm={(customPrompt) => regeneratePost(customPrompt)}
                                            disabled={isGenerating || jobState === "queued" || jobState === "delayed"}
                                        />
                                    </div>
                                </div>
                            )}

                            {/* Social Network Icons */}
                            <div className="flex gap-4 justify-center py-4 border-b border-solid border-b-[color:var(--Primary-200,#E9D7FE)]">
                                {visibleSocialIcons.length === 0 && (
                                    <div className="w-full text-center bg-yellow-100 border border-yellow-400 text-yellow-800 px-4 py-2 rounded mb-4">
                                        ⚠️ No connected social networks for this user.
                                    </div>
                                )}
                                <div className="flex gap-4 justify-center py-4 border-b border-solid border-b-[color:var(--Primary-200,#E9D7FE)]">
                                    {(visibleSocialIcons.length > 0 ? visibleSocialIcons : socialIcons).map(
                                        ({ platform, src, label }) => renderSocialIcon(platform, src, label)
                                    )}
                                </div>
                            </div>

                            <div className="space-y-4 max-w-lg w-full">
                                <h2 className="text-lg font-semibold text-center">Post for {employee.fullName}</h2>

                                {/* Post Title */}
                                <div>
                                    <h3 className="font-medium text-gray-700">Title</h3>
                                    <div className="whitespace-pre-line text-black bg-purple-100 rounded-xl p-4">
                                        {currentPost.postTitle}
                                    </div>
                                </div>

                                {/* Main Post Content */}
                                <div>
                                    <div className="flex justify-between items-center mb-2">
                                        <h3 className="font-medium text-gray-700">Post</h3>
                                        <div className="flex items-center gap-1" style={{ color: "#667085" }}>
                                            Planned
                                            <DatePicker
                                                locale={{
                                                    dateTimeFormat: localStorage.getItem("ln") || "en",
                                                }}
                                                disabledDate={(current) => current && current < moment().startOf("day")}
                                                defaultValue={dayjs(new Date(publishTime))}
                                                format="DD.MM.YYYY HH:mm"
                                                showTime
                                                onChange={(value) => value && changePublishTime(value.toISOString())}
                                            />
                                        </div>
                                        <div className="flex gap-2">
                                            <img
                                                className="cursor-pointer w-5 h-5"
                                                onClick={() => clipboardCopy(currentPost.post)}
                                                src={copyIcon}
                                                alt="copyicon"
                                            />
                                            <img
                                                className="cursor-pointer w-5 h-5"
                                                onClick={changeMainText}
                                                src={editMainText ? checkIcon : penIcon}
                                                alt="penicon"
                                            />
                                        </div>
                                    </div>

                                    {editMainText ? (
                                        <div className="relative">
                    <textarea
                        id={`mainText-${employeeId}`}
                        defaultValue={currentPost.post}
                        className="w-full h-32 p-4 text-black bg-purple-100 rounded-xl resize-none"
                        placeholder="Enter post content..."
                    />
                                            <div className="absolute -bottom-6 right-2 bg-white rounded px-2 py-1 text-xs text-gray-600">
                                                {currentPost.post.length} / 2000
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="whitespace-pre-line text-black bg-purple-100 rounded-xl p-4">
                                            {currentPost.post}
                                        </div>
                                    )}
                                </div>

                                {/* Hashtags */}
                                <div>
                                    <div className="flex justify-between items-center mb-2">
                                        <h3 className="font-medium text-gray-700">Hashtags</h3>
                                        <div className="flex gap-2">
                                            <img
                                                className="cursor-pointer w-5 h-5"
                                                onClick={() => clipboardCopy(currentPost.hashtags)}
                                                src={copyIcon}
                                                alt="copyicon"
                                            />
                                            <img
                                                className="cursor-pointer w-5 h-5"
                                                onClick={changeHashtags}
                                                src={editHashtag ? checkIcon : penIcon}
                                                alt="editicon"
                                            />
                                        </div>
                                    </div>

                                    {editHashtag ? (
                                        <textarea
                                            id={`hashtags-${employeeId}`}
                                            defaultValue={currentPost.hashtags}
                                            className="w-full h-20 p-4 text-black bg-purple-100 rounded-xl resize-none"
                                            placeholder="Enter hashtags..."
                                        />
                                    ) : (
                                        <div className="text-black bg-purple-100 rounded-xl p-4">
                                            {currentPost.hashtags}
                                        </div>
                                    )}
                                </div>

                                {/* Media */}
                                <div>
                                    <div className="flex justify-between items-center mb-2">
                                        <h3 className="font-medium text-gray-700">Media</h3>
                                        <div className="flex gap-2">
                                            <img
                                                className="cursor-pointer w-5 h-5"
                                                onClick={() =>
                                                    clipboardCopy(
                                                        Array.isArray(currentPost.media)
                                                            ? currentPost.media.join(", ")
                                                            : currentPost.media
                                                    )
                                                }
                                                src={copyIcon}
                                                alt="copyicon"
                                            />
                                            <img
                                                className="cursor-pointer w-5 h-5"
                                                onClick={changeOffer}
                                                src={editOffer ? checkIcon : penIcon}
                                                alt="penicon"
                                            />
                                        </div>
                                    </div>

                                    {editOffer ? (
                                        <textarea
                                            id={`media-${employeeId}`}
                                            defaultValue={
                                                Array.isArray(currentPost.media)
                                                    ? currentPost.media.join(", ")
                                                    : currentPost.media
                                            }
                                            className="w-full h-24 p-4 text-black bg-purple-100 rounded-xl resize-none"
                                            placeholder="Enter media suggestions..."
                                        />
                                    ) : (
                                        <div className="text-black bg-purple-100 rounded-xl p-4">
                                            {Array.isArray(currentPost.media)
                                                ? currentPost.media.join(", ")
                                                : currentPost.media}
                                        </div>
                                    )}
                                </div>

                                {/* Upload helper */}
                                <Popover
                                    overlayStyle={{ maxWidth: 500 }}
                                    placement="bottom"
                                    content={() => (
                                        <pre
                                            style={{ fontSize: "12px" }}
                                            className="text-[10px] block text-wrap text-pretty max-w-full font-inherit text-inherit"
                                        >
                    Recommended image/video formats and platform-specific limits.
                  </pre>
                                    )}
                                    title="Media Requirements"
                                >
                                    <div className="flex justify-center gap-1 items-center mb-2">
                                        <QuestionCircleOutlined className="bg-primary text-white p-1 rounded-full text-md cursor-help transition-opacity opacity-50 hover:opacity-100" />
                                        <h3 className="font-medium text-gray-700 cursor-help">Image</h3>
                                    </div>
                                </Popover>

                                {/* File Upload Area */}
                                <div
                                    onDragEnter={handleDragEnter}
                                    onDragLeave={handleDragLeave}
                                    onDragOver={handleDragOver}
                                    onDrop={handleDrop}
                                    onClick={tempImages.length ? null : handleClick}
                                    className={`${
                                        tempImages.length
                                            ? "cursor-pointer"
                                            : "cursor-pointer border-2 border-dashed border-gray-400"
                                    } ${dragActive ? "border-violet-600 bg-violet-50" : ""} p-4 text-black bg-purple-100 rounded-xl min-h-24`}
                                >
                                    {tempImages.length ? (
                                        <div className="space-y-3">
                                            {tempImages.map((image, index) => (
                                                <div
                                                    key={index}
                                                    className={index < tempImages.length - 1 ? "border-b border-gray-300 pb-3" : ""}
                                                >
                                                    <img
                                                        className="mx-auto rounded-lg max-w-full h-auto max-h-48 object-contain"
                                                        alt="preview image"
                                                        src={URL.createObjectURL(image)}
                                                    />
                                                    <div className="mt-2 flex items-center justify-between">
                                                        <p
                                                            className={`text-sm ${
                                                                (image.size / 1024).toFixed() > 10000 ? "text-red-500" : "text-gray-600"
                                                            }`}
                                                        >
                                                            {(image.size / 1024).toFixed(2)} KB
                                                            {(image.size / 1024).toFixed() > 10000 && (
                                                                <span className="block text-xs">Large file size</span>
                                                            )}
                                                        </p>
                                                        <button
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleDeleteImage(index);
                                                            }}
                                                            className="bg-red-500 text-white text-xs px-3 py-1 rounded hover:bg-red-600"
                                                        >
                                                            Remove
                                                        </button>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <div className="text-center py-8">
                                            <p className="text-lg text-gray-600">No images yet</p>
                                            <p className="text-sm text-gray-500 mt-1">
                                                {dragActive ? "Drop files to upload" : "Drag & drop or click to upload"}
                                            </p>
                                            <small className="text-xs text-gray-400 block mt-2">
                                                PNG/JPG up to 8MB each, total 80MB.
                                            </small>
                                        </div>
                                    )}

                                    {tempVideo && (
                                        <div className="mt-4">
                                            <video
                                                className="mx-auto rounded-lg"
                                                ref={videoPlayerRef}
                                                width="320"
                                                height="240"
                                                controls
                                            >
                                                <source src={URL.createObjectURL(tempVideo)} type="video/mp4" />
                                                Your browser does not support the video tag.
                                            </video>
                                            <div className="mt-2 text-center">
                                                <p
                                                    className={`text-sm ${
                                                        (tempVideo.size / 1000000).toFixed() > 200 ? "text-red-500" : "text-gray-600"
                                                    }`}
                                                >
                                                    {(tempVideo.size / 1000000).toFixed(2)} MB
                                                    {(tempVideo.size / 1000000).toFixed() > 200 && (
                                                        <span className="block text-xs">Large file size</span>
                                                    )}
                                                </p>
                                            </div>
                                        </div>
                                    )}
                                </div>

                                {/* Action Buttons */}
                                <div className="flex justify-center mt-6">
                                    {isGenerating ? (
                                        <div className="text-center py-4">
                                            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
                                            <p className="mt-2 text-gray-600">Processing image…</p>
                                        </div>
                                    ) : (
                                        <div className="grid grid-cols-2 gap-3 w-full max-w-md">
                                            {tempImages.length > 0 && (
                                                <button
                                                    onClick={handleResetImages}
                                                    className="bg-red-500 text-white px-4 py-2 rounded-lg hover:bg-red-600 transition-colors"
                                                >
                                                    Remove images
                                                </button>
                                            )}

                                            <button
                                                onClick={handleClick}
                                                className="bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600 transition-colors"
                                            >
                                                Upload image
                                            </button>

                                            {tempVideo ? (
                                                <button
                                                    className="bg-red-500 text-white px-4 py-2 rounded-lg hover:bg-red-600 transition-colors"
                                                    onClick={handleRemoveVideo}
                                                >
                                                    Remove video
                                                </button>
                                            ) : (
                                                <button
                                                    disabled
                                                    className="bg-gray-400 text-white px-4 py-2 rounded-lg cursor-not-allowed"
                                                    onClick={() => hiddenVideoInput.current.click()}
                                                >
                                                    Add video
                                                </button>
                                            )}

                                            <PostRegenerateEditor onConfirm={(customPrompt) => regeneratePost(customPrompt)} />
                                        </div>
                                    )}
                                </div>

                                {/* Submit */}
                                <div className="text-center mt-8">
                                    <button
                                        type="button"
                                        onClick={() => submitPost(autopublication)}
                                        disabled={isGenerating || currentPost.sent || currentPost.socialNetwork.length === 0}
                                        className={`${
                                            currentPost.sent || currentPost.socialNetwork.length === 0
                                                ? "bg-gray-400 cursor-not-allowed"
                                                : "bg-green-500 hover:bg-green-600"
                                        } text-white px-8 py-3 rounded-lg transition-colors text-lg font-medium`}
                                    >
                                        {isGenerating ? "Sending…" : `Send to ${employee.fullName}`}
                                    </button>
                                    <label
                                        style={{
                                            marginTop: "30px",
                                            display: "flex",
                                            alignItems: "center",
                                            gap: "8px",
                                            cursor: "pointer",
                                            padding: "6px 10px",
                                            borderRadius: "8px",
                                            border: autopublication ? "2px solid #6c63ff" : "2px solid #ccc",
                                            backgroundColor: autopublication ? "#f0edff" : "#fafafa",
                                            userSelect: "none",
                                            transition: "all 0.2s ease",
                                        }}
                                    >
                                        <input
                                            type="checkbox"
                                            checked={autopublication}
                                            onChange={(e) => setAutopublication(e.target.checked)}
                                        />
                                        {autopublication ? "Auto-publish" : "Auto-publish"}
                                    </label>
                                </div>

                                {currentPost.sent && (
                                    <div className="text-green-600 text-center font-medium mt-2">
                                        ✅ Post has been sent
                                    </div>
                                )}
                            </div>

                            {/* Hidden file inputs */}
                            <input
                                type="file"
                                accept="image/*"
                                onChange={handleChange}
                                ref={hiddenFileInput}
                                multiple
                                style={{ display: "none" }}
                            />
                            <input
                                onChange={handleAddVideo}
                                ref={hiddenVideoInput}
                                type="file"
                                accept="video/mp4, video/mov, video/wmv, video/avi, video/flv, video/mpeg, video/mpg, video/quicktime, video/3gpp, video/webm"
                                className="hidden"
                            />
                        </div>
                    </div>
                </div>
            </LoadingOverlay>
        </div>
    );

};

export default PostContentEditorDetailsV2;
